import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Save } from "lucide-react";
import { createClientUser, getClient } from "../../../api/chatbotApi";

const initialForm = {
  name: "",
  email: "",
  business_role: "",
  password: "",
  confirmPassword: "",
};

export default function ChatbotClientUserCreatePage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    let active = true;
    getClient(clientId)
      .then((response) => {
        if (active) setClient(response);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [clientId]);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setError("");

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      await createClientUser({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        client_id: clientId,
        business_role: form.business_role.trim(),
        role: 1,
      });
      navigate(`/clients/${clientId}/users`);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Error creating user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/clients" className="inline-flex items-center gap-1 transition hover:text-slate-900">
          <ArrowLeft size={16} /> Clients
        </Link>
        <span>/</span>
        <Link to={`/clients/${clientId}/users`} className="transition hover:text-slate-900">
          {client?.name || "Client"} Users
        </Link>
        <span>/</span>
        <span className="font-semibold text-slate-900">New User</span>
      </div>

      <form onSubmit={handleCreate} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        <div className="mb-6 border-b border-slate-100 pb-5">
          <h1 className="text-2xl font-bold text-slate-900">Add User</h1>
          <p className="mt-1 text-sm text-slate-500">Create a user for {client?.name || "this client"}.</p>
        </div>

        {error && (
          <div className="mb-5 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">Name *</label>
            <input required type="text" value={form.name} onChange={updateField("name")} className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-[#096dea] focus:outline-none" autoFocus />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">Email *</label>
            <input required type="email" value={form.email} onChange={updateField("email")} className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-[#096dea] focus:outline-none" />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">Business Role *</label>
            <input required type="text" value={form.business_role} onChange={updateField("business_role")} placeholder="e.g. Manager" className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-[#096dea] focus:outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">Password *</label>
            <input required minLength={8} type="password" value={form.password} onChange={updateField("password")} className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-[#096dea] focus:outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">Confirm Password *</label>
            <input required minLength={8} type="password" value={form.confirmPassword} onChange={updateField("confirmPassword")} className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-[#096dea] focus:outline-none" />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3 border-t border-slate-100 pt-5">
          <Link to={`/clients/${clientId}/users`} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">
            Cancel
          </Link>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb] disabled:opacity-50">
            <Save size={16} /> {saving ? "Creating..." : "Create User"}
          </button>
        </div>
      </form>
    </div>
  );
}
